/**
 * Shared financial / assumption display formatters for Execute portfolio
 * and Approval Artifacts (CFO ROI Summary, Assumptions Appendix).
 */

/** Compact millions range, always 2 decimal places: $13.14M–$27.28M */
export function formatEstimatedValueRangeM(
  lowMillions?: number | null,
  highMillions?: number | null,
  displayFallback?: string | null
): string {
  if (
    lowMillions != null &&
    highMillions != null &&
    Number.isFinite(lowMillions) &&
    Number.isFinite(highMillions)
  ) {
    return `$${lowMillions.toFixed(2)}M–$${highMillions.toFixed(2)}M`;
  }

  const raw = (displayFallback ?? '').replace(/\/yr$/i, '').trim();
  if (!raw) return '—';

  const match = raw.match(
    /\$?\s*([\d,.]+)\s*M?\s*[–\-—to]+\s*\$?\s*([\d,.]+)\s*M?/i
  );
  if (match) {
    const low = Number(match[1].replace(/,/g, ''));
    const high = Number(match[2].replace(/,/g, ''));
    if (Number.isFinite(low) && Number.isFinite(high)) {
      return `$${low.toFixed(2)}M–$${high.toFixed(2)}M`;
    }
  }

  return raw;
}

/** Full USD with commas, or compact $XM / $XK for very large values when preferred. */
export function formatCurrencyAmount(value: number, { compact = false } = {}): string {
  if (!Number.isFinite(value)) return '—';

  if (compact) {
    if (Math.abs(value) >= 1_000_000_000) {
      return `$${(value / 1_000_000_000).toFixed(1)}B`;
    }
    if (Math.abs(value) >= 1_000_000) {
      const m = value / 1_000_000;
      return `$${m % 1 === 0 ? m.toFixed(0) : m.toFixed(1)}M`;
    }
    if (Math.abs(value) >= 1_000) {
      return `$${Math.round(value / 1_000)}K`;
    }
  }

  return `$${Math.round(value).toLocaleString('en-US')}`;
}

/**
 * Format assumption values for CFO / Assumptions Appendix.
 * Handles raw API numbers that arrive without $ / % / units.
 */
export function formatAssumptionDisplayValue(
  label: string,
  value?: number | string | null,
  valueDisplay?: string | null,
  unit?: string | null
): string {
  const labelLower = (label ?? '').toLowerCase();
  const display = (valueDisplay ?? '').trim();
  const numericFromDisplay = display
    ? Number(display.replace(/[,$%\s]/g, ''))
    : NaN;
  const numeric =
    typeof value === 'number' && Number.isFinite(value)
      ? value
      : Number.isFinite(numericFromDisplay)
        ? numericFromDisplay
        : typeof value === 'string'
          ? Number(String(value).replace(/[,$%\s]/g, ''))
          : NaN;

  const unitLower = (unit ?? '').toLowerCase().trim();

  // Prefer existing rich displays that already include currency / percent / units
  if (
    display &&
    (/[$%]/.test(display) ||
      /month|week|year|day/i.test(display) ||
      (/[a-z]/i.test(display) && !/^\d/.test(display)))
  ) {
    return display;
  }

  if (!Number.isFinite(numeric)) {
    return display || (value != null ? String(value) : '—');
  }

  // Explicit unit from API
  if (unitLower === '%' || unitLower === 'percent' || unitLower === 'pct') {
    return `${formatPercentNumber(numeric)}%`;
  }
  if (unitLower.includes('month')) {
    return `${Math.round(numeric)} ${Math.round(numeric) === 1 ? 'month' : 'months'}`;
  }
  if (
    unitLower.includes('usd') ||
    unitLower.includes('dollar') ||
    unitLower === '$'
  ) {
    return formatCurrencyAmount(numeric);
  }

  // Label heuristics — percent before money (e.g. "Revenue Impact Pct")
  if (/month|ramp/.test(labelLower)) {
    return `${Math.round(numeric)} ${Math.round(numeric) === 1 ? 'month' : 'months'}`;
  }

  const looksLikePercentLabel =
    /pct|percent|%/.test(labelLower) ||
    ((/rate|gain|reduction|efficiency|adoption/.test(labelLower) ||
      (/impact/.test(labelLower) && !/revenue|cost|value|dollar/.test(labelLower))) &&
      Math.abs(numeric) <= 100);

  if (looksLikePercentLabel && Math.abs(numeric) <= 1000) {
    return `${formatPercentNumber(numeric)}%`;
  }

  const looksLikeMoneyLabel =
    /revenue|cost|value|dollar|budget|spend|benefit|investment|payroll|expense/.test(
      labelLower
    );

  if (looksLikeMoneyLabel || Math.abs(numeric) >= 1000) {
    return formatCurrencyAmount(numeric);
  }

  return display || String(numeric);
}

function formatPercentNumber(n: number): string {
  if (Number.isInteger(n)) return String(n);
  return n.toFixed(1).replace(/\.0$/, '');
}
